import React from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';

import { Columns, Column, MuiTitle } from 'src/ui/mui-ui-kit';;
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  setBreadcrumbs,
  reduceServerErrors,
  ConfirmDeletionModal,
  UiToastAdapter,
  GuidelinesCard,
} from 'src/ui';

import { getToken } from 'src/auth';
import { getUser } from 'src/user';
import { getAudits, getAuditHistory } from '../redux';
import { fetchAudits, deleteAudit, updateAudit, fetchAuditHistory, exportAudits } from '../redux/actions';
import { AuditsTable } from '../components';
import { AuditNotesModal } from '../components/AuditNotesModal';
import { AuditDateEditModal } from '../components/AuditDateEditModal';
import { AuditHistoryModal } from '../components/AuditHistoryModal';

const guidelines = [
  `Track and manage audit schedules for your accounts and locations.`,
  `Set next audit dates and track completion status.`,
  `Use notes to record audit findings and follow-up actions.`,
];

const AuditsListPage = ({
  setBreadcrumbs,
  fetchAudits,
  fetchAuditHistory,
  token,
  user,
  audits,
  auditHistory,
  deleteAudit,
  exportAudits,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [deletingAudit, setDeletingAudit] = React.useState(null);
  const [notesModalOpen, setNotesModalOpen] = React.useState(false);
  const [selectedAuditForNotes, setSelectedAuditForNotes] = React.useState(null);
  const [dateEditModalOpen, setDateEditModalOpen] = React.useState(false);
  const [selectedAuditForDateEdit, setSelectedAuditForDateEdit] = React.useState(null);
  const [selectedDateField, setSelectedDateField] = React.useState(null);
  const [pendingNotesUpdates, setPendingNotesUpdates] = React.useState({});
  const [historyModalOpen, setHistoryModalOpen] = React.useState(false);
  const [selectedAuditForHistory, setSelectedAuditForHistory] = React.useState(null);
  const [tableFilters, setTableFilters] = React.useState({});

  // Function to merge pending notes updates with audits
  const getAuditsWithPendingUpdates = React.useMemo(() => {
    if (!audits.audits) return [];

    // Filter audits by account if user has a specific account
    const filteredAudits = user.accountId
      ? audits.audits.filter(audit => audit.account === user.accountId)
      : audits.audits;

    return filteredAudits.map(audit => ({
      ...audit,
      notes: pendingNotesUpdates[audit.id] !== undefined
        ? pendingNotesUpdates[audit.id]
        : audit.notes
    }));
  }, [audits.audits, pendingNotesUpdates, user.accountId]);

  React.useEffect(() => {
    (async () => {
      setBreadcrumbs([{ label: 'Audits', href: '/audits' }]);

      if (token.isValid()) {
        await fetchAudits();

        if (audits.isError()) {
          reduceServerErrors(audits);
        }
      }
      setLoading(false);
    })();
  }, []);

  const onDeleteAudit = audit => {
    setDeletingAudit(audit);
  };

  const onCancelDeleteAudit = () => {
    setDeletingAudit(null);
  };

  const onConfirmDeleteAudit = async () => {
    setLoading(true);

    onCancelDeleteAudit();

    await deleteAudit(deletingAudit.id);

    setLoading(false);

    if (audits.isError()) {
      return reduceServerErrors(audits);
    }
    toast(UiToastAdapter('Audit deleted successfully!', 'success'));
  };

  const onViewNotes = audit => {
    setSelectedAuditForNotes(audit);
    setNotesModalOpen(true);
  };

  const onCloseNotesModal = () => {
    setNotesModalOpen(false);
    setTimeout(() => {
      setSelectedAuditForNotes(null);
    }, 0);
  };

  const onCloseDateEditModal = () => {
    setDateEditModalOpen(false);
    setTimeout(() => {
      setSelectedAuditForDateEdit(null);
      setSelectedDateField(null);
    }, 0);
  };

  const onSubmitNotes = async (values) => {
    // Store the audit ID before any potential state changes
    const auditId = selectedAuditForNotes?.id;

    if (!auditId) {
      console.error('No audit selected for notes update');
      toast(UiToastAdapter('Error: No audit selected', 'error'));
      return;
    }

    try {
      // Store the pending update immediately for instant UI feedback
      setPendingNotesUpdates(prev => ({
        ...prev,
        [auditId]: values.notes
      }));

      // Close modal immediately after submission starts
      onCloseNotesModal();

      // Call the update API
      await updateAudit(auditId, { notes: values.notes });

      if (audits.isError()) {
        // Remove pending update on error
        setPendingNotesUpdates(prev => {
          const updated = { ...prev };
          delete updated[auditId];
          return updated;
        });
        reduceServerErrors(audits);
        return;
      }

      toast(UiToastAdapter('Audit notes updated successfully!', 'success'));
    } catch (error) {
      // Remove pending update on error
      setPendingNotesUpdates(prev => {
        const updated = { ...prev };
        delete updated[auditId];
        return updated;
      });
      console.error('Error updating audit notes:', error);
      toast(UiToastAdapter('Error updating audit notes', 'error'));
    }
  };

  const onEditDate = (audit, field) => {
    setSelectedAuditForDateEdit(audit);
    setSelectedDateField(field);
    setDateEditModalOpen(true);
  };

  const onViewHistory = async (audit) => {
    setSelectedAuditForHistory(audit);
    setHistoryModalOpen(true);
    
    // Fetch history for the selected audit
    if (audit?.id) {
      await fetchAuditHistory(audit.id);
    }
  };

  const onCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setTimeout(() => {
      setSelectedAuditForHistory(null);
    }, 0);
  };

  const handleExportAudits = () => {
    exportAudits(tableFilters);
  };

  return (
    <>
      <Columns isMultiline>
        <Column isSize="1/2">
          <MuiTitle isSize="4" hasTextColor="info" tag="h2">
            Audits
          </MuiTitle>
        </Column>
        <Column isSize="1/2">
          <div className="is-pulled-right" style={{ display: 'flex', gap: '10px' }}>
            <button
              className="button is-outlined is-info"
              onClick={handleExportAudits}
              disabled={loading}
            >
              <span className="icon is-left">
                <FontAwesomeIcon icon={['fal', 'file-alt']} />
              </span>
              <span>Export to Excel</span>
            </button>
            <NavLink
              className="button is-gradient-bright is-long"
              to="/audits/add"
            >
              <span className="icon is-left">
                <FontAwesomeIcon icon={['fal', 'plus']} />
              </span>
              <span>Add Audit</span>
            </NavLink>
          </div>
        </Column>
        <Column>
          <AuditsTable
            loading={loading}
            audits={getAuditsWithPendingUpdates}
            onDeleteAudit={onDeleteAudit}
            onViewNotes={onViewNotes}
            onEditDate={onEditDate}
            onViewHistory={onViewHistory}
            onFilteredDataChange={setTableFilters}
          />
        </Column>
        {!user.isBankAdmin && !user.isSuperAdmin && (
          <Column isSize="narrow">
            <GuidelinesCard
              guidelines={guidelines}
            />
          </Column>
        )}
      </Columns>
      <ConfirmDeletionModal
        isActive={!!deletingAudit}
        onClickCancel={onCancelDeleteAudit}
        onClickConfirm={onConfirmDeleteAudit}
        title={`${(deletingAudit || {}).account_name} - ${(deletingAudit || {}).location_address}`}
      />
      <AuditNotesModal
        isActive={notesModalOpen}
        onCancel={onCloseNotesModal}
        onSubmit={onSubmitNotes}
        action="Save"
        initialNotes={getAuditsWithPendingUpdates.find(audit => audit.id === selectedAuditForNotes?.id)?.notes || ''}
      />
      <AuditDateEditModal
        isActive={dateEditModalOpen}
        onClose={onCloseDateEditModal}
        audit={selectedAuditForDateEdit}
        field={selectedDateField}
      />
      <AuditHistoryModal
        isActive={historyModalOpen}
        onClose={onCloseHistoryModal}
        history={auditHistory?.history || []}
        loading={auditHistory?.isLoading() || false}
      />
    </>
  );
};

AuditsListPage.propTypes = {
  fetchAudits: propTypes.func.isRequired,
  fetchAuditHistory: propTypes.func.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  deleteAudit: propTypes.func.isRequired,
  exportAudits: propTypes.func.isRequired,
  token: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  audits: propTypes.object.isRequired,
  auditHistory: propTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    token: getToken(state),
    user: getUser(state),
    audits: getAudits(state),
    auditHistory: getAuditHistory(state),
  };
}

const connectedAuditsListPage = connect(mapStateToProps, {
  setBreadcrumbs,
  fetchAudits,
  fetchAuditHistory,
  deleteAudit,
  updateAudit,
  exportAudits,
})(AuditsListPage);

export { connectedAuditsListPage as AuditsListPage };